import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Upload, Check, ArrowLeft } from "lucide-react";
import TutorProfileSection from "@/components/TutorProfileSection";
import { TUTOR_SERVICE_ID } from "@/lib/tutorConfig";

export default function PartnerSettings() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    services: [],
    service_location: "",
    service_radius_km: 10,
    service_pincode: "",
    pan_number: "",
    pan_file: "",
    gst_number: "",
    gst_file: "",
    licence_number: "",
    licence_file: "",
    tutor_profile: { classes: [], subjects: [] },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/partners/me")]).then(([s, p]) => {
      setServices(s.data);
      setProfile(p.data);
      setForm({
        services: p.data.services || [],
        service_location: p.data.service_location || "",
        service_radius_km: p.data.service_radius_km || 10,
        service_pincode: p.data.service_pincode || "",
        pan_number: p.data.pan_number || "",
        pan_file: p.data.pan_file || "",
        gst_number: p.data.gst_number || "",
        gst_file: p.data.gst_file || "",
        licence_number: p.data.licence_number || "",
        licence_file: p.data.licence_file || "",
        tutor_profile: p.data.tutor_profile || { classes: [], subjects: [] },
      });
    });
  }, []);

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id)
        ? f.services.filter((s) => s !== id)
        : [...f.services, id],
    }));
  };

  const uploadFile = async (file, field) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, [field]: r.data.filename }));
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/partners/me", form);
      toast.success("Settings updated");
      navigate("/partner/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar variant="dashboard" />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 fade-in">
        <button
          onClick={() => navigate("/partner/dashboard")}
          className="text-sm text-[#4A4A4A] hover:text-[#1A1A1A] inline-flex items-center gap-1 mb-6"
          data-testid="settings-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="text-xs uppercase tracking-widest text-[#E06D53] font-semibold">
          Account Settings
        </div>
        <h1 className="font-display text-4xl mt-2">Update your business details</h1>

        <form onSubmit={save} className="mt-10 space-y-8">
          <div>
            <div className="font-display-medium text-xl">Services & coverage</div>
            <p className="text-sm text-[#737373] mt-1">
              Changing services will start sending you new leads of new services.
            </p>

            <div className="mt-5">
              <label className="text-sm font-medium">Services you provide</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {services.map((s) => {
                  const active = form.services.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleService(s.id)}
                      className={`p-3 rounded-xl text-sm font-medium text-left border transition-colors ${active ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white border-[#E5E3DB] hover:border-[#1E3F20]"}`}
                      data-testid={`settings-service-${s.id}`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
              <div>
                <label className="text-sm font-medium">Service location</label>
                <input
                  className="octa-input mt-2"
                  value={form.service_location}
                  onChange={(e) => setForm({ ...form, service_location: e.target.value })}
                  data-testid="settings-location-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Service pincode</label>
                <input
                  className="octa-input mt-2"
                  value={form.service_pincode}
                  onChange={(e) => setForm({ ...form, service_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  data-testid="settings-pincode-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">
                  Service radius: {form.service_radius_km} km
                </label>
                <input
                  type="range"
                  min="2"
                  max="50"
                  value={form.service_radius_km}
                  onChange={(e) => setForm({ ...form, service_radius_km: parseInt(e.target.value) })}
                  className="w-full mt-4"
                  data-testid="settings-radius-input"
                />
              </div>
            </div>

            {form.services.includes(TUTOR_SERVICE_ID) && (
              <div className="mt-6">
                <TutorProfileSection
                  value={form.tutor_profile}
                  onChange={(tp) => setForm({ ...form, tutor_profile: tp })}
                />
              </div>
            )}
          </div>

          <div className="border-t border-[#E5E3DB] pt-8">
            <div className="font-display-medium text-xl">Optional KYC</div>
            <p className="text-sm text-[#737373] mt-1">
              Add PAN, GST, and Trade Licence for higher trust & priority.
            </p>

            {[
              { num: "pan_number", file: "pan_file", label: "PAN" },
              { num: "gst_number", file: "gst_file", label: "GST" },
              { num: "licence_number", file: "licence_file", label: "Trade/Services Licence" },
            ].map((f) => (
              <div key={f.num} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className="text-sm font-medium">{f.label} number</label>
                  <input
                    className="octa-input mt-2"
                    value={form[f.num]}
                    onChange={(e) => setForm({ ...form, [f.num]: e.target.value })}
                    data-testid={`settings-${f.num}`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Upload {f.label}</label>
                  <label className="mt-2 flex items-center gap-3 octa-input cursor-pointer">
                    <Upload className="w-4 h-4 text-[#737373]" />
                    <span className="text-sm text-[#737373]">
                      {form[f.file] ? (
                        <span className="text-[#1E3F20] inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Uploaded</span>
                      ) : "Click to upload"}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,application/pdf"
                      onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], f.file)}
                      data-testid={`settings-upload-${f.file}`}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          <button
            type="submit"
            disabled={saving}
            className="h-12 px-8 rounded-full btn-primary-octa font-semibold disabled:opacity-50"
            data-testid="settings-save-btn"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}
